<template>
	<auth-layout
		brand-desc="租户引导"
		headline="先确定租户，再进入登录与主界面"
		subtext="输入租户号后连接到对应服务实例。"
		mobile-desc="租户引导"
		:badges="[
			{ label: '安全', text: '端到端加密通信，数据安全有保障' },
			{ label: '多端', text: '支持 Web、桌面端与移动端同步使用' }
		]"
	>
		<template #preview>
			<div class="preview-card">
				<div class="preview-tenant-row">
					<div class="preview-tenant-icon"><i class="el-icon-office-building"></i></div>
					<div class="preview-tenant-info">
						<div class="preview-tenant-name">租户 A</div>
						<div class="preview-tenant-tag">已连接</div>
					</div>
				</div>
				<div class="preview-divider"></div>
				<div class="preview-tenant-row preview-tenant-row--dim">
					<div class="preview-tenant-icon preview-tenant-icon--dim"><i class="el-icon-office-building"></i></div>
					<div class="preview-tenant-info">
						<div class="preview-tenant-name">租户 B</div>
						<div class="preview-tenant-tag preview-tenant-tag--dim">切换</div>
					</div>
				</div>
				<div class="preview-status">
					<i class="el-icon-connection"></i> 独立服务实例，数据隔离
				</div>
			</div>
		</template>

		<!-- 表单卡片 -->
		<div class="auth-card">
			<div class="card-header">
				<div class="card-title">租户引导</div>
				<div class="card-desc">输入租户号后继续</div>
			</div>
			<div class="card-content">
				<el-form @submit.native.prevent="onSubmit">
					<el-form-item>
						<label class="form-label">租户号</label>
						<el-input
							v-model="tenantKey"
							placeholder="请输入租户号"
							prefix-icon="el-icon-office-building"
							@keyup.enter.native="onSubmit"
						></el-input>
						<div class="tenant-help-text">支持 3-32 位小写字母、数字或 -</div>
					</el-form-item>
					<el-button
						type="primary"
						class="submit-btn"
						:loading="loading"
						@click="onSubmit"
					>{{ loading ? '进入中…' : '确认并继续' }}</el-button>
				</el-form>
			</div>
		</div>
	</auth-layout>
</template>

<script>
import { clearAuthSession } from '../api/authSession';
import AuthLayout from '../components/common/AuthLayout.vue';
import { syncAuthWindow } from '../utils/authWindow';
import { normalizeTenantKeyInput } from '../utils/authValidators';
import { resolveTenantErrorMessage } from '../utils/tenantErrorMessage';

export default {
	name: 'TenantEntry',
	components: { AuthLayout },
	data() {
		return {
			tenantKey: '',
			loading: false,
			lastMessageMeta: {
				key: '',
				at: 0
			}
		};
	},
	methods: {
		showDedupedMessage(type, message) {
			const normalizedType = typeof type === 'string' ? type : 'info';
			const normalizedMessage = typeof message === 'string' ? message.trim() : '';
			if (!normalizedMessage) {
				return;
			}
			const now = Date.now();
			const key = `${normalizedType}:${normalizedMessage}`;
			if (this.lastMessageMeta.key === key && now - this.lastMessageMeta.at < 1200) {
				return;
			}
			this.lastMessageMeta = {
				key,
				at: now
			};
			this.$message({
				type: normalizedType,
				message: normalizedMessage
			});
		},
		normalizeRedirect(raw) {
			const value = typeof raw === 'string' ? raw : '';
			if (!value.startsWith('/')) {
				return '';
			}
			if (value.startsWith('/tenant')) {
				return '';
			}
			return value;
		},
		onSubmit() {
			if (this.loading) {
				return;
			}
			const key = normalizeTenantKeyInput(this.tenantKey);
			const previousTenantKey = this.$tenantEndpoint.getRememberedTenantKey() || '';
			this.tenantKey = key;
			if (!key) {
				this.showDedupedMessage('error', '请输入租户号');
				return;
			}
			if (!/^[a-z0-9-]{3,32}$/.test(key)) {
				this.showDedupedMessage('error', '租户号格式不正确');
				return;
			}
			this.loading = true;
			this.$tenantEndpoint.bootstrapByTenantInput(key).then((session) => {
				this.tenantKey = session.tenantKey;
				const redirectPath = this.normalizeRedirect(this.$route.query.redirect);
				const accessToken = sessionStorage.getItem('accessToken');
				const manualEntry = String(this.$route.query.manual || '') === '1';
				const tenantChanged = Boolean(previousTenantKey) && previousTenantKey !== session.tenantKey;
				if (accessToken && !manualEntry && !tenantChanged) {
					this.$router.replace(redirectPath || '/home/chat');
					return;
				}
				if (manualEntry || tenantChanged) {
					clearAuthSession();
				}
				this.$router.replace(redirectPath || '/login');
			}).catch((error) => {
				if (error && error.name === 'TenantEndpointError') {
					this.showDedupedMessage('error', resolveTenantErrorMessage(error, 'entry'));
					return;
				}
				this.showDedupedMessage('error', '租户引导失败，请稍后重试');
			}).finally(() => {
				this.loading = false;
			});
		}
	},
	mounted() {
		this.tenantKey = this.$tenantEndpoint.getRememberedTenantKey() || '';
		this.$elm.setTitleTip('', {
			userName: ''
		});
		syncAuthWindow();
	},
	watch: {
		tenantKey(value) {
			const normalized = normalizeTenantKeyInput(value);
			if (normalized !== value) {
				this.tenantKey = normalized;
			}
		}
	}
};
</script>

<style scoped lang="scss">
@import "../assets/style/auth-card";

.tenant-help-text {
	margin-top: 6px;
	font-size: 12px;
	line-height: 1.4;
	color: var(--im-text-color-light);
}

// 预览插图
.preview-card {
	width: 100%;
	max-width: 280px;
	background: var(--im-card-bg-80);
	border: 1px solid rgba(255, 255, 255, 0.12);
	border-radius: 16px;
	padding: 20px 16px 16px;
	backdrop-filter: blur(8px);
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.preview-tenant-row {
	display: flex;
	align-items: center;
	gap: 10px;

	&--dim { opacity: 0.5; }
}

.preview-tenant-icon {
	width: 36px;
	height: 36px;
	border-radius: 10px;
	background: var(--im-color-primary);
	color: #fff;
	display: grid;
	place-items: center;
	font-size: 16px;
	flex-shrink: 0;

	&--dim {
		background: var(--im-background-active);
		color: var(--im-text-color-light);
	}
}

.preview-tenant-info {
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
}

.preview-tenant-name {
	font-size: 13px;
	font-weight: 500;
	color: var(--im-text-color);
}

.preview-tenant-tag {
	font-size: 11px;
	padding: 2px 8px;
	border-radius: 9999px;
	background: rgba(var(--im-color-primary-rgb, 64, 158, 255), 0.12);
	color: var(--im-color-primary);
	white-space: nowrap;

	&--dim {
		background: var(--im-background-active);
		color: var(--im-text-color-light);
	}
}

.preview-divider {
	height: 1px;
	background: rgba(var(--im-black-rgb), 0.06);
}

.preview-status {
	text-align: center;
	font-size: 11px;
	color: var(--im-text-color-light);
	margin-top: 4px;

	i {
		margin-right: 4px;
		font-size: 11px;
	}
}
</style>
